// AM Advertising Home — the alert evaluation. PURE + unit-tested (spine in →
// typed alerts out), free of rendering concerns so Phase 2's Slack digest can
// reuse it verbatim server-side.
//
// ⚠️⚠️ THE FRESHNESS GUARD IS THE LOAD-BEARING RULE, AND IT HAS TWO HALVES.
// Every window a rule reads ends at `evaluationDay` — NEVER at a later date,
// and never at the newest date either.
//
//   evaluationDay = min(platform asOf, utcToday - 1)
//
// ① THE PLATFORM BOUND (`asOf`, the latest date with any rows roster-wide).
//   Meta is 2-hourly and Google's transfer is daily, so on any given morning
//   one platform lags the other; evaluated against today, the lagging platform
//   would read as an entire roster of no-spend alerts. Mutation-checked: a
//   lagging platform produces ZERO false no-spend alerts.
//
// ② THE COMPLETE-DAY BOUND (`utcToday - 1`, 2026-09-15). ⚠⚠ THE PLATFORM BOUND
//   ALONE IS NOT ENOUGH, and the header used to claim it was — "an unloaded day
//   is simply not evaluated" is true of a whole PLATFORM and false of a single
//   ACCOUNT. `asOf` carries no account filter, so the first account anywhere to
//   record spend on a new day drags the ENTIRE roster onto a day that is hours
//   old, and `spendOn` returns 0 for an account with no row yet. Two healthy
//   clubs were flagged red for it. The guard measured the platform axis while
//   the account axis was broken — so the mutation check for ② is deliberately
//   per-ACCOUNT (one account with a today-row, one without).
//
//   ⚠️ `utcToday - 1` NEEDS NO TIMEZONE HANDLING, and that is why it is stated in
//   UTC rather than per account. Kyck operates entirely in UTC+ zones (AU +8…
//   +11, NZ +12/+13, Singapore +8), and a UTC+ zone reaches the end of a day
//   EARLIER in UTC terms than UTC does — so a day finished in UTC has finished
//   everywhere we operate. No DST, no per-account timezone, no elapsed-hours
//   threshold to tune. Today is not a settled day anywhere in the chain: Zernio's
//   own UI shows the last 7 days ENDING YESTERDAY.
//
//   ⚠️ THE `min` IS LOAD-BEARING: Google's transfer lands whole days, so it must
//   keep being judged at its own asOf and lose no freshness. A bound that always
//   subtracted a day would cost Google a day for nothing. Mutation-checked.
//
// ⚠️⚠️ ALERT EVALUATION ONLY. The book table, the pulse charts and the spend
// figures go on showing today's partial data — that is useful and correct. The
// page computes its own pacing for the table column from the raw asOf.
//
// Channels are INDEPENDENT throughout: every rule evaluates meta and google
// separately and every alert names its channel.

import type {
  AdAlertSeverity,
  AdAlertType,
  AdChannel,
  HomeAccount,
  HomeAccountHealth,
  HomeAsOf,
} from '../../types/advertisingHome.js';
import { formatReportingAmount } from '../reportingMoney.js';
import {
  CPL_SPIKE_AMBER_RATIO,
  CPL_SPIKE_RED_RATIO,
  DROUGHT_DAYS,
  DROUGHT_MIN_LEADS_30D,
  NO_LEADS_WINDOW_DAYS,
  NO_SPEND_LOOKBACK_DAYS,
  PACING_DEVIATION,
  SPIKE_MIN_LEADS_30D,
  SPIKE_MIN_SPEND_7D,
} from './guards.js';
import {
  addDaysYmd,
  channelDays,
  dayOfMonth,
  daysInMonthOf,
  hasRowOn,
  minYmd,
  monthStartOf,
  spendOn,
  sumWindow,
  windowCpl,
  type SpineIndex,
} from './spine.js';
import type { SpineDay } from '../../types/advertisingHome.js';
import { classifyCpl, type BrandAverages } from './noLeads.js';

// The vocabulary lives in the shared wire types (an acknowledgement persists an
// alert's type + severity, so storage and evaluation must share one definition).
// Re-exported under the names every caller already uses.
export type AlertType = AdAlertType;
export type AlertSeverity = AdAlertSeverity;

export interface HomeAlert {
  type: AlertType;
  severity: AlertSeverity;
  accountId: string;
  accountName: string;
  channel: AdChannel;
  /** One-line human reason. */
  reason: string;
  /** The key figure, formatted in the account-channel's native currency. */
  figure: string;
}

export interface ChannelPacing {
  /** projected month-end spend ÷ budget (1 = exactly on budget). */
  pace: number;
  projected: number;
  budget: number;
  mtdSpend: number;
  currency: string;
}

export interface AlertEvaluation {
  alerts: HomeAlert[];
  /** Every book account gets an entry ('ontrack' when nothing fired). */
  healthByAccount: Map<string, HomeAccountHealth>;
}

export interface AlertInputs {
  accounts: readonly HomeAccount[];
  spine: SpineIndex;
  asOf: HomeAsOf;
  /**
   * Today's UTC date (YYYY-MM-DD) — the complete-day bound's other half.
   *
   * ⚠️ REQUIRED, WITH NO DEFAULT, AND THE MODULE READS NO CLOCK ITSELF. The
   * caller supplies it, so this stays pure and unit-testable at any date, and a
   * new caller that forgets is a COMPILE ERROR rather than one silently
   * inheriting a wrong "today". A default would reintroduce exactly the
   * silent-inheritance class this fix exists to remove.
   *
   * ⚠️ It must be the UTC date, not a local one. A UTC+10 caller passing its
   * OWN date would make `utcToday - 1` land on a day UTC has not finished — the
   * partial day this bound exists to exclude.
   */
  utcToday: string;
  /** The pacing toggle — off = pacing alerts are not evaluated at all. */
  includePacing: boolean;
  /**
   * Brand window averages over ALL accounts in each brand — the "spending, no
   * leads" threshold. ⚠️ REQUIRED, no default: an omitted one would quietly turn
   * every threshold into the fallback, so a caller that forgets fails to compile.
   */
  brandAverages: BrandAverages;
}

const SEVERITY_ORDER: Record<AlertSeverity, number> = { red: 0, amber: 1 };
const TYPE_ORDER: Record<AlertType, number> = {
  no_spend: 0,
  spend_no_leads: 1,
  cpl_spike: 2,
  lead_drought: 3,
  pacing: 4,
};

function channelActive(account: HomeAccount, channel: AdChannel): boolean {
  // Alerts fire for ACTIVE accounts only — a paused account not spending is
  // the intended state, not an incident.
  if (account.status !== 'active') return false;
  return channel === 'meta' ? account.runsMeta : account.runsGoogle;
}

function budgetCentsFor(account: HomeAccount, channel: AdChannel): number | null {
  return channel === 'meta' ? account.metaMonthlyBudgetCents : account.googleMonthlyBudgetCents;
}

/**
 * Month-to-date pacing for one channel, evaluated at that platform's asOf.
 * Null when there is no budget (pacing is NEVER computed without one), no
 * loaded data, or the month has not meaningfully started.
 */
export function computeChannelPacing(
  days: readonly SpineDay[],
  budgetCents: number | null,
  asOfDay: string | null,
): ChannelPacing | null {
  if (budgetCents === null || budgetCents <= 0 || asOfDay === null) return null;
  const mtd = sumWindow(days, monthStartOf(asOfDay), asOfDay);
  const elapsed = dayOfMonth(asOfDay);
  if (elapsed <= 0) return null;
  const projected = (mtd.spend / elapsed) * daysInMonthOf(asOfDay);
  const budget = budgetCents / 100; // master budgets are integer cents
  return {
    pace: projected / budget,
    projected: Math.round(projected * 100) / 100,
    budget,
    mtdSpend: mtd.spend,
    currency: mtd.currency ?? 'AUD',
  };
}

/**
 * The last day that is BOTH loaded for this platform and actually finished.
 *
 *     evaluationDay = min(asOfDay, utcToday - 1)
 *
 * ⚠️⚠️ THE `min` IS THE WHOLE POINT and must not be simplified to a bare
 * `utcToday - 1`. Google's transfer lands whole days, so its asOf is ALREADY a
 * finished day and clamping it further would cost it a day of freshness for
 * nothing; a platform lagging further back keeps being judged at its own latest
 * loaded day. Only a platform whose asOf has run ahead onto today — Meta, every
 * day, because `asOf` is roster-wide — is pulled back. Mutation-checked.
 *
 * Null in, null out: nothing loaded for this platform means nothing evaluated.
 */
export function completeDayBound(asOfDay: string | null, utcToday: string): string | null {
  if (asOfDay === null) return null;
  return minYmd(asOfDay, addDaysYmd(utcToday, -1));
}

function evaluateChannel(
  account: HomeAccount,
  days: readonly SpineDay[],
  channel: AdChannel,
  /**
   * ⚠️⚠️ ALREADY BOUNDED by `completeDayBound` — the raw asOf is
   * deliberately NOT in scope here. All four rules derive their windows from
   * this one date, so "every rule judges a finished day" is STRUCTURAL rather
   * than four separate things to remember: there is no unbounded date a rule
   * could reach for by mistake.
   */
  evaluationDay: string | null,
  includePacing: boolean,
  brandAverages: BrandAverages,
): HomeAlert[] {
  // ⚠️ The freshness guard: nothing loaded for this platform, or no finished
  // day to judge → nothing is evaluated. Every window below ends AT
  // evaluationDay, never later.
  if (evaluationDay === null) return [];

  const alerts: HomeAlert[] = [];
  const base = { accountId: account.id, accountName: account.name, channel };
  const w7 = sumWindow(days, addDaysYmd(evaluationDay, -6), evaluationDay);
  const w30 = sumWindow(days, addDaysYmd(evaluationDay, -29), evaluationDay);
  const currency = w30.currency ?? account.currency;
  const money = (n: number): string => formatReportingAmount(n, currency);

  // ── Not spending (red): nothing on the last FINISHED day, for a channel that
  // WAS spending (the lookback guard keeps dormant accounts out).
  //
  // ⚠️⚠️ ABSENCE ≠ A MEASURED ZERO, AND THE DIFFERENCE IS REPORTED RATHER
  // THAN ACTED ON. Rows are written 1:1 from Zernio's sparse-active-days
  // `daily[]`, which carries an entry per day a campaign RAN — and a zero-spend
  // day INSIDE a run does get a row (probe-verified). So "no row" is produced
  // BOTH by an account whose figures have not arrived and by one whose
  // campaigns have ended, and nothing in the data separates them.
  //
  // ⚠️⚠️ SO DO NOT SUPPRESS ON ABSENCE. A stop IS an absence in this model,
  // and a suppression keyed on the surrounding days was tried and REVERTED: a
  // genuinely stopped account alerted on the first day (its data still ended at
  // the day before) and then went SILENT for the rest of the lookback week, as
  // the evaluation day moved past its last row. One day of warning and then
  // nothing is the failure this rule exists to prevent, and it is invisible —
  // the account simply stops appearing.
  //
  // What the distinction is good for is the WORDING: "we have a row and it says
  // zero" sends an AM to the ad account, "no figures arrived for that day" sends
  // them to Sync health. Both stay red — the money is not going out either way,
  // and downgrading one would bury it.
  const loaded = hasRowOn(days, evaluationDay);
  const latest = spendOn(days, evaluationDay);
  if (latest === 0) {
    const lookback = sumWindow(
      days,
      addDaysYmd(evaluationDay, -NO_SPEND_LOOKBACK_DAYS),
      addDaysYmd(evaluationDay, -1),
    );
    if (lookback.spend > 0) {
      alerts.push({
        ...base,
        type: 'no_spend',
        severity: 'red',
        reason: loaded
          ? `No spend on ${evaluationDay}, the last full day`
          : `No figures arrived for ${evaluationDay}, the last full day`,
        figure: loaded
          ? `${money(lookback.spend)} across the prior ${NO_SPEND_LOOKBACK_DAYS} days, then ${money(0)}`
          : `${money(lookback.spend)} across the prior ${NO_SPEND_LOOKBACK_DAYS} days, then nothing recorded`,
      });
    }
  }

  // ── Spending, no leads (red) — THE ONE RULE, in noLeads.ts. The last
  // NO_LEADS_WINDOW_DAYS days, this channel: zero leads and spend at or above the
  // brand's average CPL (all accounts in the brand, this channel, this currency).
  {
    const wNoLeads = sumWindow(
      days,
      addDaysYmd(evaluationDay, -(NO_LEADS_WINDOW_DAYS - 1)),
      evaluationDay,
    );
    const cur = wNoLeads.currency ?? account.currency;
    const spendCur = wNoLeads.spendByCurrency.find((x) => x.currency === cur)?.amount ?? 0;
    const leadsCur = wNoLeads.leadsByCurrency.find((x) => x.currency === cur)?.amount ?? 0;
    const verdict = classifyCpl({
      spend: spendCur,
      leads: leadsCur,
      brandCpl: brandAverages.cpl(account.brandAccountId, channel, cur),
    });
    if (verdict.kind === 'no_leads') {
      const m = (n: number): string => formatReportingAmount(n, cur);
      alerts.push({
        ...base,
        type: 'spend_no_leads',
        severity: 'red',
        reason: `Spending, no leads in ${NO_LEADS_WINDOW_DAYS} days`,
        figure: verdict.usedFallback
          ? `${m(verdict.spend)} spent, 0 leads (brand has no leads; ${m(verdict.threshold)} floor)`
          : `${m(verdict.spend)} spent, 0 leads (brand average CPL ${m(verdict.threshold)})`,
      });
    }
  }

  // ── CPL spike (amber; red at extreme): 7-day CPL vs the account's OWN
  // 30-day CPL. Volume-guarded so small accounts don't cry wolf.
  const cpl7 = windowCpl(w7);
  const cpl30 = windowCpl(w30);
  if (
    cpl7 !== null &&
    cpl30 !== null &&
    w7.spend >= SPIKE_MIN_SPEND_7D &&
    w30.leads >= SPIKE_MIN_LEADS_30D
  ) {
    const ratio = cpl7 / cpl30;
    if (ratio >= CPL_SPIKE_AMBER_RATIO) {
      alerts.push({
        ...base,
        type: 'cpl_spike',
        severity: ratio >= CPL_SPIKE_RED_RATIO ? 'red' : 'amber',
        reason: `CPL ${ratio >= CPL_SPIKE_RED_RATIO ? 'way ' : ''}above its own 30-day norm (${Math.round(ratio * 100) / 100}×)`,
        figure: `${money(cpl7)} (7d) vs ${money(cpl30)} (30d)`,
      });
    }
  }

  // ── Lead drought (amber): money going out on every one of the last
  // DROUGHT_DAYS loaded days, nothing coming in — for an account whose 30-day
  // history shows it normally gets leads.
  if (w30.leads >= DROUGHT_MIN_LEADS_30D) {
    const droughtFrom = addDaysYmd(evaluationDay, -(DROUGHT_DAYS - 1));
    let everyDaySpent = true;
    for (let i = 0; i < DROUGHT_DAYS; i++) {
      if (spendOn(days, addDaysYmd(droughtFrom, i)) <= 0) {
        everyDaySpent = false;
        break;
      }
    }
    const drought = sumWindow(days, droughtFrom, evaluationDay);
    if (everyDaySpent && drought.leads === 0) {
      alerts.push({
        ...base,
        type: 'lead_drought',
        severity: 'amber',
        reason: `Spending every day, zero leads for ${DROUGHT_DAYS} days`,
        figure: `${money(drought.spend)} spent, 0 leads`,
      });
    }
  }

  // ── Budget pacing (amber): ONLY where a master budget exists, and only when
  // the whole pacing feature is toggled on.
  if (includePacing) {
    const pacing = computeChannelPacing(days, budgetCentsFor(account, channel), evaluationDay);
    if (pacing !== null && Math.abs(pacing.pace - 1) > PACING_DEVIATION) {
      const pct = Math.round(pacing.pace * 100);
      alerts.push({
        ...base,
        type: 'pacing',
        severity: 'amber',
        reason:
          pacing.pace > 1
            ? `Tracking to ${pct}% of the monthly budget (overspend)`
            : `Tracking to ${pct}% of the monthly budget (underspend)`,
        figure: `${formatReportingAmount(pacing.projected, pacing.currency)} projected vs ${formatReportingAmount(pacing.budget, pacing.currency)} budget`,
      });
    }
  }

  return alerts;
}

/** Evaluate the whole book. Deterministic order: severity → type → account name. */
export function evaluateAlerts(input: AlertInputs): AlertEvaluation {
  const alerts: HomeAlert[] = [];
  const healthByAccount = new Map<string, HomeAccountHealth>();

  for (const account of input.accounts) {
    const accountAlerts: HomeAlert[] = [];
    for (const channel of ['meta', 'google'] as const) {
      if (!channelActive(account, channel)) continue;
      const days = channelDays(input.spine, account.id, channel);
      const asOfDay = channel === 'meta' ? input.asOf.meta : input.asOf.google;
      // ⚠️ The bound is applied HERE, once, and the raw asOf never travels any
      // further — see `evaluateChannel`'s parameter note.
      accountAlerts.push(
        ...evaluateChannel(
          account,
          days,
          channel,
          completeDayBound(asOfDay, input.utcToday),
          input.includePacing,
          input.brandAverages,
        ),
      );
    }
    alerts.push(...accountAlerts);
    const health: HomeAccountHealth = accountAlerts.some((a) => a.severity === 'red')
      ? 'critical'
      : accountAlerts.length > 0
        ? 'attention'
        : 'ontrack';
    healthByAccount.set(account.id, health);
  }

  alerts.sort(
    (a, b) =>
      SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
      TYPE_ORDER[a.type] - TYPE_ORDER[b.type] ||
      a.accountName.localeCompare(b.accountName),
  );

  return { alerts, healthByAccount };
}
